import { ArrowPathIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { API_URL, WEB_URL, WWW_URL } from "../../../.env.current";
import { useGlobalState } from "../../util/useGlobalState";
import { HomePageTitle } from "../common/Title";

const NAVIGATION_FOOTER = {
  essentials: [
    { name: "Start", href: "/" },
  ],
  information: [
    { name: "Pricing", href: "/pricing" },
  ],
  account: [
    { name: "Sign up", href: `${WEB_URL}/signup` },
    { name: "Log in", href: `${WEB_URL}/login` },
    { name: "Reset password", href: `${WEB_URL}/reset` },
  ],
  legal: [
    { name: "Terms of service", href: "/terms" },
    { name: "Privacy policy", href: "/privacy" },
  ],
  social: [
    {
      name: "X",
      href: "https://x.com/nataliemarleny",
      icon: (props: any) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path d="M13.6823 10.6218L20.2391 3H18.6854L12.9921 9.61788L8.44486 3H3.2002L10.0765 13.0074L3.2002 21H4.75404L10.7663 14.0113L15.5685 21H20.8131L13.6819 10.6218H13.6823ZM11.5541 13.0956L10.8574 12.0991L5.31391 4.16971H7.70053L12.1742 10.5689L12.8709 11.5655L18.6861 19.8835H16.2995L11.5541 13.096V13.0956Z" />
        </svg>
      ),
    },
    {
      name: "Bluesky",
      href: "https://bsky.app/profile/nataliemarleny.com",
      icon: (props: any) => (
        <svg fill="currentColor" viewBox="0 0 600 530" {...props}>
          <path d="m135.72 44.03c66.496 49.921 138.02 151.14 164.28 205.46 26.262-54.316 97.782-155.54 164.28-205.46 47.98-36.021 125.72-63.892 125.72 24.795 0 17.712-10.155 148.79-16.111 170.07-20.703 73.984-96.144 92.854-163.25 81.433 117.3 19.964 147.14 86.092 82.697 152.22-122.39 125.59-175.91-31.511-189.63-71.766-2.514-7.3797-3.6904-10.832-3.7077-7.8964-0.0174-2.9357-1.1937 0.51669-3.7077 7.8964-13.714 40.255-67.233 197.36-189.63 71.766-64.444-66.128-34.605-132.26 82.697-152.22-67.108 11.421-142.55-7.4491-163.25-81.433-5.9562-21.282-16.111-152.36-16.111-170.07 0-88.687 77.742-60.816 125.72-24.795z" />
        </svg>
      ),
    },
    {
      name: "GitHub",
      href: "https://github.com/nataliemarleny",
      icon: (props: any) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    {
      name: "YouTube",
      href: "https://youtube.com/@nataliemarleny",
      icon: (props: any) => (
        <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
          <path
            fillRule="evenodd"
            d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 0 1-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 0 1-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 0 1 1.768-1.768C5.744 5 11.998 5 11.998 5s6.255 0 7.814.418ZM15.194 12 10 15V9l5.194 3Z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
  ],
};

export default function MarketingLayout({ children, bgPattern = false }: { children: ReactNode; bgPattern?: boolean }) {
  const [globalState, setGlobalState] = useGlobalState();

  const router = useRouter();
  const NAVIGATION_MAIN = useMemo(
    () => [
      {
        name: "Start",
        url: new URL(`${WWW_URL}/`),
        isActive: () => new URL(`${WWW_URL}/`).pathname === router.pathname,
        isExternal: false,
      },
      {
        name: "Pricing",
        url: new URL(`${WWW_URL}/pricing`),
        isActive: () => new URL(`${WWW_URL}/pricing`).pathname === router.pathname,
        isExternal: false,
      },
    ],
    [router.pathname]
  );

  // --- Mailing list -------------------------------------------------------
  const [mailState, setMailState] = useState({
    subscribing: false,
    emailInput: "",
    emailSubscribed: null as string | null,
  });

  const handleMailSubscribe = useCallback(
    async (e: any) => {
      e.preventDefault();
      setMailState((c) => ({ ...c, subscribing: true }));
      await new Promise((r) => setTimeout(r, 2000));
      const params = new URLSearchParams({ email: mailState.emailInput });
      const href = `${API_URL}/v1/public/mailing-list/subscribe?${params}`;
      try {
        const res = await fetch(href, { method: "POST" });
        if (!res.ok) throw new Error();
        setMailState((c) => ({ ...c, subscribing: false, emailSubscribed: c.emailInput }));
      } catch {
        setMailState((c) => ({ ...c, subscribing: false }));
      }
    },
    [mailState.emailInput]
  );

  return (
    <>
      <div
        className={classNames(
          "h-full w-full relative",
          bgPattern ? "bg-gradient-to-br from-gray-50 via-white to-rose-50" : "bg-white"
        )}
      >
        {/* Content wrapper */}
        <div className="relative z-10">
          {/* Header */}
          <header
            className={classNames(
              "transition duration-300 sticky top-0 z-50 flex justify-center",
              bgPattern ? "bg-white/80 backdrop-blur-sm" : "bg-white opacity-89"
            )}
          >
            <nav
              aria-label="Global"
              className="w-full max-w-7xl flex justify-between pt-2.5 sm:pt-3 pb-1.5 sm:pb-2.5 pl-4 lg:pl-8 pr-3.5 lg:pr-4.5"
            >
              <div className="flex flex-col sm:flex-row gap-y-[3px] sm:gap-y-0 sm:gap-x-10 items-start">
                <div className="w-[145px]">
                  <HomePageTitle />
                </div>
                <div className="mt-px sm:mt-0.5 -ml-2 sm:-ml-2.5 flex items-baseline gap-x-px sm:gap-x-3">
                  {NAVIGATION_MAIN.map((item) => {
                    const isCurrent = item?.isActive?.();
                    return (
                      <Link
                        key={item.name}
                        href={item.url.href}
                        className={classNames(
                          isCurrent ? "text-black font-semibold" : "text-gray-600 hover:underline font-medium",
                          "px-2 sm:px-2.5 py-1 sm:py-2 rounded-md text-base",
                          "flex items-center gap-x-1.5"
                        )}
                        aria-current={isCurrent ? "page" : undefined}
                      >
                        {item.name}
                        {item.isExternal && (
                          <ArrowTopRightOnSquareIcon className="inline h-4 w-auto" aria-hidden="true" />
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
              <div className="lg:mr-[1vw] flex flex-col sm:flex-row lg:flex-1 lg:justify-end items-center gap-y-1 sm:gap-y-0 sm:gap-x-[5px] max-h-[60px]">
                <Link
                  href={`${WEB_URL}/dashboard?${new URLSearchParams({ pathVia: "/login" })}`}
                  className="w-full sm:w-auto text-center bg-white rounded sm:rounded-md border-2 border-indigo-600 hover:border-indigo-500 px-2.5 sm:px-3.5 py-[6px] sm:py-[7px] text-sm font-semibold text-indigo-600 hover:text-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 whitespace-nowrap"
                >
                  Log in
                </Link>
                <Link
                  href={`${WEB_URL}/dashboard?${new URLSearchParams({ pathVia: "/signup" })}`}
                  className="w-full sm:w-auto text-center rounded sm:rounded-md bg-indigo-600 px-2.5 sm:px-3.5 py-[8px] sm:py-[9px] text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 whitespace-nowrap"
                >
                  Sign up
                </Link>
              </div>
            </nav>
          </header>

          <main className="relative min-h-[65vh] isolate">{children}</main>

          {/* Footer */}
          <footer className="bg-white">
            <div
              className={classNames(
                "mx-auto max-w-7xl px-6 lg:px-8 pt-24 sm:pt-28 lg:pt-32",
                Intl.DateTimeFormat().resolvedOptions().timeZone.startsWith("Europe/") &&
                  !globalState.loadingLocalStorage &&
                  globalState.cookieResponse === undefined
                  ? "pb-22"
                  : "pb-4"
              )}
            >
              <div className="border-t border-gray-900/20 pt-14 sm:pt-18 pb-2 xl:grid xl:grid-cols-5 xl:gap-x-28">
                <div className="xl:col-span-2 flex flex-col justify-between">
                  <div className="flex gap-x-6">
                    {NAVIGATION_FOOTER.social.map((item) => (
                      <a key={item.name} href={item.href} className="text-gray-600 hover:text-gray-800 z-10">
                        <span className="sr-only">{item.name}</span>
                        <item.icon aria-hidden="true" className="size-6" />
                      </a>
                    ))}
                  </div>

                  <div className="mt-14">
                    <h2 className="text-sm/6 font-semibold text-gray-900">Subscribe for updates</h2>
                    {!mailState.emailSubscribed ? (
                      <form className="mt-3 h-[42px] max-w-md" onSubmit={handleMailSubscribe}>
                        <div className="flex gap-x-3">
                          <label htmlFor="email-address" className="sr-only">
                            Email address
                          </label>
                          <input
                            disabled={mailState.subscribing}
                            id="email-address"
                            name="email"
                            type="email"
                            required
                            placeholder="Enter your email"
                            autoComplete="email"
                            className="min-w-0 max-w-[calc(100vw-170px)] flex-auto rounded-md bg-white px-3.5 py-2 text-base text-gray-900 outline outline-1 outline-gray-100 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                            value={mailState.emailInput}
                            onChange={(e) => setMailState((c) => ({ ...c, emailInput: e.target.value }))}
                          />
                          <button
                            disabled={mailState.subscribing}
                            type="submit"
                            className={classNames(
                              mailState.subscribing && "animate-pulse",
                              "flex-none rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                            )}
                          >
                            Subscribe
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className="mt-2 h-[42px] flex gap-x-3 justify-start items-center">
                        <div className="text-gray-700">
                          Confirmation email has been sent to{" "}
                          <span className="font-mono font-semibold">{mailState.emailSubscribed}</span>
                        </div>
                        <button
                          type="button"
                          className="p-0.5 bg-white text-indigo-700 hover:text-indigo-800"
                          onClick={() => {
                            setMailState((c) => ({ ...c, emailSubscribed: null }));
                          }}
                        >
                          <ArrowPathIcon className="h-5 w-auto" aria-hidden="true" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-16 grid grid-cols-2 gap-8 xl:col-span-3 xl:mt-0">
                  <div className="md:grid md:grid-cols-2 md:gap-8">
                    <div>
                      <h3 className="text-sm/6 font-semibold text-gray-900">Essentials</h3>
                      <ul role="list" className="mt-5.5 space-y-[13px]">
                        {NAVIGATION_FOOTER.essentials.map((item) => (
                          <li key={item.name}>
                            <a href={item.href} className="text-sm/6 text-gray-600 hover:text-gray-900">
                              {item.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-10 md:mt-0">
                      <h3 className="text-sm/6 font-semibold text-gray-900">Information</h3>
                      <ul role="list" className="mt-5.5 space-y-[13px]">
                        {NAVIGATION_FOOTER.information.map((item) => (
                          <li key={item.name}>
                            <a href={item.href} className="text-sm/6 text-gray-600 hover:text-gray-900">
                              {item.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="md:grid md:grid-cols-2 md:gap-8">
                    <div>
                      <h3 className="text-sm/6 font-semibold text-gray-900">Account</h3>
                      <ul role="list" className="mt-5.5 space-y-[13px]">
                        {NAVIGATION_FOOTER.account.map((item) => (
                          <li key={item.name}>
                            <a href={item.href} className="text-sm/6 text-gray-600 hover:text-gray-900">
                              {item.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-10 md:mt-0">
                      <h3 className="text-sm/6 font-semibold text-gray-900">Legal</h3>
                      <ul role="list" className="mt-5.5 space-y-[13px]">
                        {NAVIGATION_FOOTER.legal.map((item) => (
                          <li key={item.name}>
                            <a href={item.href} className="text-sm/6 text-gray-600 hover:text-gray-900">
                              {item.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-900/20 pb-10 sm:pb-14 pt-10 sm:pt-12 mt-10 sm:mt-12">
                <p className="text-sm/6 text-gray-600">&copy; 2025 Gitlip. All rights reserved.</p>
              </div>
            </div>
          </footer>
        </div>
      </div>
      <style jsx global>{`
        html {
          overflow-y: scroll;
        }
      `}</style>

      {/* Cookie Banner */}
      {Intl.DateTimeFormat().resolvedOptions().timeZone.startsWith("Europe/") &&
        !globalState.loadingLocalStorage &&
        globalState.cookieResponse === undefined && (
          <div className="z-40 fixed inset-x-0 bottom-0 border-t border-gray-900/30 bg-white shadow-lg">
            <div className="mx-auto max-w-7xl flex flex-col justify-between gap-x-10 gap-y-4 p-6 md:flex-row md:items-center lg:px-8">
              <p className="max-w-4xl text-sm/6 text-gray-900">
                We value your privacy. This website uses <span className="font-semibold">essential cookies</span> for
                functionality and <span className="font-semibold">analytics cookies</span> to understand how you use our
                site. We don&apos;t sell your data or use tracking for advertising. See our{" "}
                <Link href="/privacy" className="font-semibold text-indigo-600 hover:text-indigo-500">
                  privacy policy
                </Link>
                .
              </p>
              <div className="flex flex-none flex-col sm:flex-row items-center gap-y-3 gap-x-5">
                <button
                  type="button"
                  onClick={() => setGlobalState((prev) => ({ ...prev, cookieResponse: false }))}
                  className="w-full sm:w-auto text-sm/6 font-semibold text-gray-900 hover:text-gray-700"
                >
                  Reject analytics
                </button>
                <button
                  type="button"
                  onClick={() => setGlobalState((prev) => ({ ...prev, cookieResponse: true }))}
                  className="w-full sm:w-auto rounded-md bg-gray-900 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-gray-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900"
                >
                  Accept all
                </button>
              </div>
            </div>
          </div>
        )}
    </>
  );
}
